/**
 * ShortlinkOptions — SMSWiser shortlink settings for a drop: where the click
 * id goes (path / subdomain) and the URL scheme (none / https), with a live
 * preview of the resulting link.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Rendered inside DropOptionsSection. Values may be "unchanged" (bulk edits:
 * keep each drop's own setting) or "mixed" (selected drops disagree — no pill
 * is active until the user picks one).
 */

import type { FC, ReactNode } from 'react';
import { Info, Link2 } from 'lucide-react';
import {
  CLICK_ID_STRUCTURE_OPTIONS,
  DOMAIN_SCHEME_OPTIONS,
  type ShortlinkClickIdStructure,
  type ShortlinkDomainScheme,
} from '@trafficpulse/shared';

import { SegmentedPills, Tooltip, type SegmentedPillOption } from '@/components/ui';
import { cn } from '@/lib/utils';

export type ShortlinkOptionValue<T extends string> = T | 'unchanged' | 'mixed';

export interface ShortlinkOptionsProps {
  clickIdStructure: ShortlinkOptionValue<ShortlinkClickIdStructure>;
  setClickIdStructure: (v: ShortlinkOptionValue<ShortlinkClickIdStructure>) => void;
  domainScheme: ShortlinkOptionValue<ShortlinkDomainScheme>;
  setDomainScheme: (v: ShortlinkOptionValue<ShortlinkDomainScheme>) => void;
  allowUnchanged?: boolean;
  disabled?: boolean;
  compact?: boolean;
}

const KEEP = { value: 'unchanged', label: 'Keep' } as const;

const CLICK_ID_TIP =
  'Where the unique click id goes in the shortlink: after the domain (Path) or in front of it (Subdomain).';
const SCHEME_TIP = 'Whether the shortlink starts with https:// (Https) or has no scheme (None).';

function pillOptions<T extends string>(
  options: ReadonlyArray<{ value: T; label: string }>,
  allowUnchanged: boolean,
): SegmentedPillOption<ShortlinkOptionValue<T>>[] {
  const base = options.map((o) => ({ value: o.value, label: o.label }));
  return allowUnchanged ? [...base, KEEP] : base;
}

/** "https://abc123.domain.com" — null when either value is not concrete. */
export function previewShortlink(
  structure: ShortlinkOptionValue<ShortlinkClickIdStructure>,
  scheme: ShortlinkOptionValue<ShortlinkDomainScheme>,
): string | null {
  const concrete = (v: string) => v !== 'unchanged' && v !== 'mixed';
  if (!concrete(structure) || !concrete(scheme)) return null;
  const prefix = scheme === 'https' ? 'https://' : '';
  const host = structure === 'subdomain' ? 'abc123.domain.com' : 'domain.com/abc123';
  return `${prefix}${host}`;
}

const Row: FC<{ label: string; tip: string; mixed: boolean; children: ReactNode }> = ({
  label,
  tip,
  mixed,
  children,
}) => (
  <div className="flex flex-col items-start gap-1">
    <span className="flex items-center gap-1.5 text-xs text-foreground">
      {label}
      <Tooltip content={tip} side="right">
        <Info className="h-3 w-3 shrink-0 text-muted-foreground" aria-label="Info" />
      </Tooltip>
      {mixed && <span className="text-[10px] italic text-muted-foreground">— mixed</span>}
    </span>
    {children}
  </div>
);

export const ShortlinkOptions: FC<ShortlinkOptionsProps> = ({
  clickIdStructure,
  setClickIdStructure,
  domainScheme,
  setDomainScheme,
  allowUnchanged = false,
  disabled = false,
  compact = false,
}) => {
  const preview = previewShortlink(clickIdStructure, domainScheme);
  return (
    <fieldset
      disabled={disabled}
      data-testid="shortlink-options"
      className={cn(
        'min-w-0 rounded-md border border-border/60 bg-muted/20',
        compact ? 'space-y-2 p-2' : 'space-y-2.5 p-3',
        disabled && 'opacity-60',
      )}
    >
      <legend className="sr-only">Shortlink</legend>
      <div className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
        <Link2 className="h-3 w-3" aria-hidden />
        Shortlink
      </div>
      <Row label="Click ID structure" tip={CLICK_ID_TIP} mixed={clickIdStructure === 'mixed'}>
        <SegmentedPills
          options={pillOptions(CLICK_ID_STRUCTURE_OPTIONS, allowUnchanged)}
          value={clickIdStructure}
          onChange={setClickIdStructure}
        />
      </Row>
      <Row label="Domain scheme" tip={SCHEME_TIP} mixed={domainScheme === 'mixed'}>
        <SegmentedPills
          options={pillOptions(DOMAIN_SCHEME_OPTIONS, allowUnchanged)}
          value={domainScheme}
          onChange={setDomainScheme}
        />
      </Row>
      <p className="truncate text-[11px] text-muted-foreground" aria-live="polite">
        {preview ? (
          <>
            Links look like{' '}
            <code className="rounded bg-background px-1 py-0.5 font-mono text-[10.5px] text-foreground">
              {preview}
            </code>
          </>
        ) : (
          'Each drop keeps its own shortlink format.'
        )}
      </p>
    </fieldset>
  );
};

export default ShortlinkOptions;
